import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { INPUT_CLASS, CHIP_CLASS } from './uiConstants';

interface ChipInputProps {
  values: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  placeholder?: string;
  /** Per-chip validity → invalid chips render red. */
  validate?: (value: string | number) => boolean;
  /** Quick-add preset tokens shown as a small dropdown. */
  presets?: { label: string; value: string }[];
  numeric?: boolean; // store numeric tokens as numbers when they parse
}

export const ChipInput: React.FC<ChipInputProps> = ({ values, onChange, placeholder, validate, presets, numeric }) => {
  const [draft, setDraft] = useState('');

  const coerce = (raw: string): string | number => {
    const t = raw.trim();
    if (numeric && /^\d+$/.test(t)) return Number(t);
    return t;
  };

  const addToken = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    const token = coerce(t);
    if (values.some(v => String(v) === String(token))) { setDraft(''); return; }
    onChange([...values, token]);
    setDraft('');
  };

  const removeAt = (i: number) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {values.map((v, i) => {
          const ok = validate ? validate(v) : true;
          return (
            <span
              key={`${v}-${i}`}
              className={ok ? CHIP_CLASS : `${CHIP_CLASS} !bg-red-50 !text-red-600 !border-red-300 dark:!bg-red-900/20 dark:!text-red-400`}
              title={ok ? undefined : 'Invalid token'}
            >
              {String(v)}
              <button type="button" onClick={() => removeAt(i)} className="hover:text-red-500" aria-label={`Remove ${v}`}>
                <X size={12} />
              </button>
            </span>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addToken(draft); }
            else if (e.key === 'Backspace' && !draft && values.length) removeAt(values.length - 1);
          }}
          onBlur={() => draft && addToken(draft)}
          placeholder={placeholder}
          className={INPUT_CLASS}
        />
        <button
          type="button"
          onClick={() => addToken(draft)}
          className="shrink-0 p-2 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-500 hover:text-teal-600 hover:border-teal-400"
          aria-label="Add token"
        >
          <Plus size={16} />
        </button>
      </div>
      {presets && presets.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {presets.map(p => (
            <button
              key={p.value}
              type="button"
              onClick={() => addToken(p.value)}
              className="px-2 py-0.5 text-[11px] rounded-md border border-dashed border-slate-300 dark:border-slate-700 text-slate-500 hover:text-teal-600 hover:border-teal-400"
            >
              + {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
